import moment from 'moment'
import { TidewaysContext } from '../../app/context'
import { ServiceType } from '../ServiceType'

export interface HistoryCriteria {
    environment: string
    service: string
    serviceType: ServiceType
    date: moment.Moment
    granularity: Granularity
}

export const enum Granularity {
    day = 'day',
    week = 'week',
    month = 'month',
}

export const calcDays = (date: moment.Moment, granularity: Granularity): string[] => {
    if (granularity === Granularity.day) {
        return [date.format('YYYY-MM-DD')]
    }

    if (granularity === Granularity.week || granularity === Granularity.month) {
        const startOf: moment.unitOfTime.StartOf = granularity === Granularity.week ? 'isoWeek' : 'month'
        const start = date.clone().startOf(startOf)
        const end = date.clone().endOf(startOf)

        let day = start
        const days: string[] = []
        while (day <= end) {
            days.push(day.format('YYYY-MM-DD'))
            day = day.clone().add(1, 'd')
        }

        return days
    }

    return []
}

export const historyCriteriaDescription = (criteria: HistoryCriteria): string => {
    if (criteria.granularity === 'day') {
        return moment(criteria.date).format('DD.MM.YYYY')
    }

    if (criteria.granularity === 'month') {
        return moment(criteria.date).format('MMMM YYYY')
    }

    const days = calcDays(criteria.date, criteria.granularity)

    const start = moment(days[0]).format('DD.MM.')
    const end = moment(days[days.length - 1]).format('DD.MM.YYYY')

    return `Week ${start} to ${end}`
}

export const aggregatedServiceReportKey = (criteria: HistoryCriteria): string => {
    const days = calcDays(criteria.date, criteria.granularity)

    return [criteria.environment, criteria.service, criteria.serviceType, days[0], criteria.granularity].join('/')
}

export const transactionReportKey = (criteria: HistoryCriteria, transactionId: number): string => {
    const days = calcDays(criteria.date, criteria.granularity)

    return [criteria.environment, criteria.service, criteria.serviceType, days[0], criteria.granularity, transactionId].join('/')
}

export const historyTrendCriteria = (criteria: HistoryCriteria): HistoryCriteria[] =>
    [4, 3, 2, 1].map(index => ({ ...criteria, date: criteria.date.clone().subtract(index, criteria.granularity) }))

export const createHistoryCriteria = (tidewaysContext: TidewaysContext): HistoryCriteria => {
    const { environment, service } = tidewaysContext
    // do not do UTC here, since it will mess with startOf/endOf in lots of cases
    const date = tidewaysContext.route.parameters.date ? moment(tidewaysContext.route.parameters.date) : moment().subtract(1, 'w')
    const granularity = (tidewaysContext.route.parameters.granularity || Granularity.week) as Granularity

    const serviceType = (tidewaysContext.route.parameters.st as ServiceType) ?? ServiceType.php

    return { environment, service, serviceType, date, granularity }
}
